// src/pages/PrivacyPolicy.jsx
import React from "react";

export default function PrivacyPolicy() {
  return (
    <div className="bg-white text-black min-h-screen">
      <div className="max-w-3xl mx-auto px-4 py-12 md:py-16 text-sm leading-relaxed md:text-base md:leading-relaxed">
        <header className="mb-10">
          <h1 className="text-2xl md:text-3xl font-serif mb-2">
            Politique de confidentialité
          </h1>
          <div className="text-gray-500 text-xs md:text-sm">
            Dernière mise à jour : juillet 2026
          </div>
        </header>

        <section className="space-y-6 text-gray-800">
          <p>
            La présente Politique de confidentialité décrit la manière dont
            <strong> Artkiv </strong> (exploité par QUANG &amp; CO,
            231 rue Saint-Honoré, 75001 Paris) collecte, utilise et protège les
            données personnelles de ses utilisateurs.
          </p>

          <p>
            Artkiv est une plateforme ArtTech qui permet aux collectionneurs,
            experts, family offices et assureurs de gérer, valoriser et
            sécuriser leurs œuvres. Nos services combinent intelligence
            artificielle, blockchain et expertise humaine pour assurer la
            traçabilité, la transmission et la valorisation des collections
            privées.
          </p>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">1. Données collectées</h2>
          <p>
            Nous collectons uniquement les informations nécessaires au
            fonctionnement des services Artkiv.
          </p>

          <div className="space-y-3">
            <h3 className="font-medium">a. Données d’identification</h3>
            <ul className="list-disc ml-5 text-gray-700">
              <li>Nom, prénom, adresse e-mail</li>
              <li>Mot de passe chiffré</li>
              <li>Numéro de téléphone (facultatif)</li>
              <li>
                Coordonnées de facturation et informations liées au paiement.
                (Les numéros de carte sont traités par notre prestataire de
                paiement sécurisé. Artkiv ne stocke pas vos numéros de carte.)
              </li>
            </ul>
          </div>

          <div className="space-y-3">
            <h3 className="font-medium">b. Données liées à la collection</h3>
            <ul className="list-disc ml-5 text-gray-700">
              <li>
                Informations sur les œuvres : titre, artiste, médium,
                dimensions, image, estimation, provenance, certificats.
              </li>
              <li>
                Localisation de l’œuvre (lieu de stockage ou d’exposition).
              </li>
              <li>
                Historique de mouvement, transport, prêt, vente et assurance.
              </li>
              <li>Documents associés (factures, certificats, expertises).</li>
            </ul>
          </div>

          <div className="space-y-3">
            <h3 className="font-medium">
              c. Données liées aux relations professionnelles
            </h3>
            <ul className="list-disc ml-5 text-gray-700">
              <li>
                Coordonnées des experts, assureurs, transporteurs, family
                offices, notaires ou autres professionnels que vous invitez ou
                mandatez via Artkiv.
              </li>
              <li>IBAN d’experts ou partenaires pour le règlement.</li>
            </ul>
          </div>

          <div className="space-y-3">
            <h3 className="font-medium">
              d. Données sensibles ou patrimoniales
            </h3>
            <p className="text-gray-700">
              Certaines informations sont considérées comme hautement
              sensibles, notamment :
            </p>
            <ul className="list-disc ml-5 text-gray-700">
              <li>Valeur estimée ou valeur d’assurance d’une œuvre,</li>
              <li>Adresse de stockage,</li>
              <li>Informations relatives à la transmission ou aux héritiers.</li>
            </ul>
            <p className="text-gray-700">
              Ces données font l’objet d’une protection renforcée. Elles ne sont
              jamais partagées avec un tiers sans votre action explicite.
            </p>
          </div>

          <div className="space-y-3">
            <h3 className="font-medium">e. Données techniques</h3>
            <ul className="list-disc ml-5 text-gray-700">
              <li>
                Informations de connexion : adresse IP, type d’appareil,
                navigateur.
              </li>
              <li>
                Journal d’activité (accès à vos œuvres, partages effectués,
                téléchargements).
              </li>
              <li>
                Cookies techniques et analytiques (voir Politique Cookies).
              </li>
            </ul>
          </div>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">2. Finalités du traitement</h2>
          <p>Nous utilisons vos données pour :</p>
          <ul className="list-disc ml-5 text-gray-700 space-y-2">
            <li>
              Fournir et maintenir les services Artkiv (gestion de collection,
              génération de rapports, certification, estimation, facturation).
            </li>
            <li>
              Garantir la traçabilité et la sécurité documentaire des œuvres.
            </li>
            <li>
              Permettre le partage sécurisé d’informations avec des experts,
              assureurs, transporteurs, notaires ou autres professionnels que
              vous mandatez.
            </li>
            <li>
              Personnaliser votre expérience et assurer l’assistance technique.
            </li>
            <li>
              Produire des statistiques internes et améliorer la qualité du
              service.
            </li>
            <li>
              Répondre à nos obligations légales (facturation, prévention de la
              fraude, obligations comptables).
            </li>
          </ul>
        </section>

        {/* 🔒 NOUVELLE SECTION : Accès & Statistiques anonymisées */}
        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">
            2 bis. Accès aux œuvres & Statistiques anonymisées
          </h2>

          <h3 className="font-medium">a. Accès aux œuvres</h3>
          <p className="text-gray-700">
            Par défaut, les fiches œuvre (images, valeurs, provenance,
            documents) sont visibles uniquement par le{" "}
            <strong>propriétaire du compte</strong>. Vous pouvez donner accès à
            un professionnel (expert, assureur, transporteur, family office,
            maison de ventes, notaire) via une invitation explicite depuis votre
            tableau de bord. Chaque partage précise le périmètre (collection ou
            œuvre) et le niveau d’accès (lecture ou édition), et peut être
            révoqué à tout moment.
          </p>
          <p className="text-gray-700">
            Artkiv applique un principe de <strong>confidentialité stricte</strong>{" "}
            et de <strong>cloisonnement des données</strong> : les équipes Artkiv{" "}
            <strong>ne disposent pas d’un accès libre ou permanent</strong> au
            contenu de vos œuvres (images, documents, valeurs, provenance).
           </p>
          <p className="text-gray-700">
            Un accès exceptionnel peut être nécessaire (support, sécurité,
            expertise ou intervention technique) uniquement s’il est{" "}
            <strong>strictement encadré</strong>, <strong>limité au besoin</strong>{" "}
            et <strong>déclenché dans le cadre d’une autorisation explicite</strong>{" "}
            ou d’une action de votre part (ex. : mandat d’assistance).
          </p>

          <h3 className="font-medium">b. Accès exceptionnel & traçabilité</h3>
          <p className="text-gray-700">
            Tout accès exceptionnel aux données d’une œuvre fait l’objet d’une{" "}
            <strong>journalisation</strong> afin de garantir la transparence et
            la sécurité. Cette traçabilité peut inclure l’identité du consultant,
            la date et l’heure, la raison de l’accès et le type d’intervention
            réalisée.
          </p>
          <p className="text-gray-700">
            Ces informations peuvent être mises à disposition du collectionneur
            sur demande ou via son espace personnel.
          </p>

          <h3 className="font-medium">c. Traitements automatisés & IA</h3>
          <p className="text-gray-700">
            Certains services (estimation, analyse visuelle, conservation,
            classification, extraction documentaire) peuvent reposer sur des{" "}
            <strong>traitements automatisés</strong> et de l’<strong>intelligence artificielle</strong>.
            Ces traitements sont réalisés <strong>sans consultation humaine systématique</strong>{" "}
            des œuvres et sont limités aux seules données nécessaires à l’exécution
            du service demandé.
          </p>
          <p className="text-gray-700">
	    Aucune décision produisant des effets juridiques ou patrimoniaux
	    significatifs n’est prise
            sans intervention humaine qualifiée lorsque cela est requis par la
            réglementation.
          </p>

          <h3 className="font-medium">d. Statistiques anonymisées (opt-in)</h3>
          <p className="text-gray-700">
            Pour améliorer le service sans compromettre votre confidentialité,
            Artkiv peut produire des statistiques agrégées sur la base des
            œuvres pour lesquelles vous avez activé l’option{" "}
            <em>“Autoriser l’utilisation anonyme de cette œuvre dans les
            statistiques globales”</em>.
          </p>
          <ul className="list-disc ml-5 text-gray-700 space-y-2">
            <li>
              Les statistiques sont{" "}
              <strong>anonymisées et agrégées</strong> : aucune identification
              d’une œuvre ou d’un propriétaire n’est possible.
            </li>
            <li>
              Les montants sont <strong>arrondis</strong> (paliers) et les
              résultats ne sont publiés que lorsque{" "}
              <strong>au moins 3 œuvres</strong> composent une même catégorie
              (règle de <em>k-anonymité</em>).
            </li>
            <li>
              Les agrégats portent sur des catégories générales telles que{" "}
              <strong>médium</strong>, <strong>artiste</strong> (sans listing
              d’œuvres) et <strong>période</strong> (ex. : Old Masters, Modern,
              Contemporary).
            </li>
            <li>
              Vous pouvez <strong>activer ou désactiver</strong> cette option
              œuvre par œuvre, à tout moment. Les statistiques se mettent à
              jour périodiquement.
            </li>
          </ul>
          <p className="text-gray-700">
            En aucun cas ces statistiques ne contiennent des titres, images,
            valeurs exactes, descriptions, identifiants d’œuvres ou d’utilisateurs.
          </p>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">3. Base légale (RGPD)</h2>
          <p>Le traitement de vos données repose sur :</p>
          <ul className="list-disc ml-5 text-gray-700 space-y-2">
            <li>
              <strong>Exécution du contrat :</strong> nous ne pouvons pas vous
              fournir le service sans certaines informations (création de
              compte, gestion d’œuvres, paiements).
            </li>
            <li>
              <strong>Intérêt légitime :</strong> sécuriser la plateforme,
              prévenir les abus, améliorer l’ergonomie.
            </li>
            <li>
              <strong>Consentement explicite :</strong> toute mise en relation
              avec des tiers (assureur, transporteur, maison de ventes,
              expert), toute analyse IA avancée, ainsi que la participation{" "}
              <strong>aux statistiques anonymisées</strong> sont soumises à
              votre accord préalable et révocable.
            </li>
          </ul>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">4. Durée de conservation</h2>
          <p className="text-gray-700">
            Vos données sont conservées pendant toute la durée d’utilisation du
            service et jusqu’à trois (3) ans après la dernière activité.
          </p>
          <p className="text-gray-700">
            Certaines informations légales (facturation, preuve
            contractuelle) peuvent être conservées plus longtemps si la loi
            nous l’impose.
          </p>
          <p className="text-gray-700">
            Les données de mesure de nos campagnes — les événements du parcours
            « Collection &amp; transmission », y compris l’adresse e-mail lorsque
            vous choisissez de la communiquer à l’issue du test — sont conservées
            treize (13) mois maximum, puis supprimées automatiquement. Si vous
            créez un compte, votre adresse e-mail est par ailleurs conservée au
            titre de ce compte, selon les durées indiquées ci-dessus.
          </p>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">
            5. Traçabilité et blockchain
          </h2>
          <p className="text-gray-700">
            Pour garantir l’authenticité et l’historique d’une œuvre, certains
            éléments techniques (par exemple l’empreinte numérique d’un
            certificat, un identifiant unique d’œuvre, une date de
            certification) peuvent être inscrits sur une blockchain.
          </p>
          <p className="text-gray-700">Ces enregistrements :</p>
          <ul className="list-disc ml-5 text-gray-700 space-y-2">
            <li>ne contiennent pas vos données personnelles,</li>
            <li>
              n’incluent pas l’adresse de stockage ni la valeur d’assurance,
            </li>
            <li>
              ne révèlent pas votre identité ou celle de vos héritiers,
            </li>
            <li>
              sont techniquement permanents et ne peuvent pas être modifiés
              après enregistrement.
            </li>
          </ul>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">
            6. Intelligence artificielle (Vision.artkiv.com)
          </h2>
          <p className="text-gray-700">
            L’utilisation du module Vision.artkiv.com et d’autres outils
            d’analyse par intelligence artificielle nécessite un consentement
            spécifique.
          </p>
          <p className="text-gray-700">
            Ces modules peuvent analyser les images, les documents ou les
            métadonnées d’une œuvre afin d’identifier des caractéristiques,
            d’aider à la description ou à l’estimation.
          </p>
          <p className="text-gray-700">
            Artkiv ne réutilise pas vos images et documents pour entraîner ou
            améliorer des modèles destinés à d’autres clients sans votre accord
            explicite. Les copies techniques créées pour l’analyse sont
            temporaires.
          </p>
          <p className="text-gray-700">
            Ces analyses sont réalisées par notre sous-traitant{" "}
            <strong>OpenAI</strong> (voir la section « Sous-traitants et
            transferts internationaux »), <strong>uniquement si vous avez
            activé le consentement à l’analyse par IA</strong>. Sans ce
            consentement, aucun document n’est transmis à un service d’IA : vos
            fichiers restent stockés et sont traités manuellement. Les données
            transmises à l’API d’OpenAI ne sont pas utilisées pour entraîner ses
            modèles.
          </p>
          <p className="text-gray-700">
            <strong>Ce qui est transmis.</strong> Vos images et documents sont
            stockés en Europe (Paris). Lorsque vous lancez une analyse par IA,
            les images ou documents concernés sont transmis à notre prestataire
            d’IA (OpenAI), dont le traitement peut avoir lieu hors de l’Union
            européenne (notamment aux États-Unis), dans le cadre des garanties
            décrites à la section « Sous-traitants et transferts
            internationaux ». Nous ne transmettons que les éléments nécessaires
            à l’analyse demandée : l’image ou le document et, selon
            l’analyse, la description de l’œuvre. Pour l’analyse de style d’une
            image, le titre de l’œuvre et le nom de l’artiste ne sont pas
            transmis. Une information vous est rappelée avant chaque lancement
            d’analyse. Le prestataire d’IA peut conserver de manière
            sécurisée les données transmises par son interface (API) pendant
            une durée maximale de 30 jours, pour la fourniture du service et la
            détection des abus, puis les supprime.
          </p>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">7. Partage des données</h2>
          <p className="text-gray-700">Nous ne revendons pas vos données.</p>
          <p className="text-gray-700">Vos données peuvent être partagées :</p>
          <ul className="list-disc ml-5 text-gray-700 space-y-2">
            <li>
              Avec des prestataires techniques (hébergement, facturation,
              messagerie sécurisée) strictement nécessaires au service.
            </li>
            <li>
              Avec des professionnels que VOUS avez explicitement invités :
              expert indépendant, assureur, transporteur, notaire, conseil
              patrimonial, family office, maison de ventes.
            </li>
            <li>
              Si la loi l’exige, avec une autorité administrative ou judiciaire
              compétente.
            </li>
          </ul>
          <p className="text-gray-700">
            Chaque partage fait partie d’une action explicite de votre part
            (ex. : “inviter cet expert à consulter cette œuvre”).
          </p>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">
            7 bis. Sous-traitants et transferts internationaux
          </h2>
          <p className="text-gray-700">
            Pour fournir le service, Artkiv fait appel à des sous-traitants
            techniques, chacun encadré par un accord de traitement des données
            (DPA) et limité aux seules données nécessaires à sa mission :
          </p>
          <ul className="list-disc ml-5 text-gray-700 space-y-2">
            <li>
              <strong>Hébergement, base de données et stockage sécurisé des
              documents</strong> — serveurs situés dans l’
              <strong>Union européenne (Paris et Helsinki)</strong> : base de
              données et stockage des documents à Paris (Supabase),
              hébergement des sites et de l’application à Paris (Hostinger),
              services d’analyse à Helsinki (Hetzner).
            </li>
            <li>
              <strong>OpenAI</strong> — analyse par intelligence artificielle
              des images et documents que vous soumettez (classification,
              description, aide à l’estimation), <strong>uniquement avec votre
              consentement</strong>. Les données transmises via l’API ne sont
              pas utilisées pour entraîner ses modèles. Hébergement aux
              États-Unis.
            </li>
            <li>
              <strong>Stripe</strong> — traitement des paiements et de la
              facturation (Artkiv ne stocke pas vos numéros de carte).
            </li>
            <li>
              Prestataire d’envoi des <strong>e-mails transactionnels</strong>{" "}
              (confirmations, notifications).
            </li>
          </ul>
          <p className="text-gray-700">
            Vos documents et votre base de données sont hébergés{" "}
            <strong>dans l’Union européenne (Paris et Helsinki)</strong>. Seuls
            certains
            traitements spécifiques — l’<strong>analyse par intelligence
            artificielle</strong> (avec votre consentement) et le{" "}
            <strong>paiement</strong> — font intervenir des prestataires situés
            aux États-Unis. Ces transferts sont encadrés par les garanties
            appropriées prévues par le RGPD, en particulier les{" "}
            <strong>clauses contractuelles types</strong> de la Commission
            européenne (et, le cas échéant, le cadre{" "}
            <em>EU–US Data Privacy Framework</em>).
          </p>
          <p className="text-gray-700">
            La liste des sous-traitants peut évoluer. En cas d’ajout d’un
            nouveau sous-traitant traitant vos données, nous mettrons à jour la
            présente politique.
          </p>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">
            8. Sécurité et stockage
          </h2>
          <ul className="list-disc ml-5 text-gray-700 space-y-2">
            <li>Données hébergées sur des serveurs sécurisés.</li>
            <li>
              Chiffrement des données en transit (HTTPS/TLS) et au repos.
            </li>
            <li>
              Sauvegardes et journalisation d’accès pour limiter les risques de
              perte ou de fraude.
            </li>
            <li>
              Accès interne restreint aux seules personnes habilitées et
              soumises à une obligation de confidentialité.
            </li>
          </ul>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">9. Cookies</h2>
          <p className="text-gray-700">
            Nous utilisons des cookies techniques nécessaires au fonctionnement
            du site (connexion, sécurité, paiement). Avec votre accord, nous
            pouvons aussi utiliser des cookies analytiques anonymisés pour
            mesurer l’audience et améliorer le service. Aucune finalité
            publicitaire n’est poursuivie.
          </p>
          <p className="text-gray-700">
            Vous pouvez gérer vos préférences cookies à tout moment via la
            bannière ou la page Cookies.
          </p>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">10. Vos droits</h2>
          <p className="text-gray-700">
            Conformément au Règlement (UE) 2016/679 (RGPD), vous disposez des
            droits suivants :
          </p>
          <ul className="list-disc ml-5 text-gray-700 space-y-2">
            <li>Droit d’accès et de rectification</li>
            <li>Droit à l’effacement (droit à l’oubli)</li>
            <li>
              Droit à la portabilité (export de vos données et documents)
            </li>
            <li>
              Droit de limitation ou d’opposition à certains traitements
            </li>
            <li>
              Droit de retirer votre consentement à tout moment, notamment pour
              l’IA, le partage à un tiers, et la participation aux statistiques
              anonymisées.
            </li>
          </ul>
          <p className="text-gray-700">
            Pour exercer vos droits : <strong>privacy@artkiv.com</strong>  
            ou par courrier : QUANG &amp; CO — 231 rue Saint-Honoré,
            75001 Paris.
          </p>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">
            11. Export & suppression de compte
          </h2>
          <p className="text-gray-700">Vous pouvez à tout moment :</p>
          <ul className="list-disc ml-5 text-gray-700 space-y-2">
            <li>
              Télécharger vos données (œuvres, documents associés) dans un
              format exploitable.
            </li>
            <li>
              Supprimer votre compte et demander la suppression des données qui
              ne sont pas soumises à une obligation légale de conservation.
            </li>
          </ul>
          <p className="text-gray-700">
            Nous nous engageons à procéder à la suppression définitive dans un
            délai raisonnable (maximum 30 jours hors obligations légales).
          </p>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">12. Modifications</h2>
          <p className="text-gray-700">
            Cette Politique de confidentialité peut évoluer pour tenir compte
            des évolutions réglementaires, techniques ou fonctionnelles du
            service. En cas de changement significatif, nous vous informerons
            via la plateforme ou par e-mail.
          </p>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">
            13. Contact protection des données
          </h2>
          <p className="text-gray-700">
            Délégué à la protection des données :{" "}
            <strong>privacy@artkiv.com</strong>
          </p>
          <p className="text-gray-700">
            Autorité de contrôle : Commission Nationale de l’Informatique et
            des Libertés (CNIL).
          </p>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">14. Langue faisant foi</h2>
          <p className="text-gray-700">
            La version française des accords juridiques et des politiques est
            considérée comme la seule version actuelle et valide de ce document.
            Toute version traduite n’est fournie que pour votre commodité, afin de
            faciliter la lecture et la compréhension de la version française. Les
            versions traduites ne sont pas juridiquement contraignantes et ne
            peuvent remplacer les versions françaises. En cas de désaccord ou de
            conflit, les accords et politiques juridiques en langue française
            prévaudront.
          </p>
        </section>

        <footer className="mt-12 text-[11px] text-gray-400 leading-relaxed">
          Artkiv — Plateforme de gestion patrimoniale d’œuvres d’art.
          L’art ne se collectionne pas, il se transmet.
        </footer>
      </div>
    </div>
  );
}
